(() => {
  if (window.cwsCartPage) return;
  window.cwsCartPage = true;

  // KK-1861: the sticky order summary sits under the pinned CWS header. The header does not set
  // the theme --height-header variable, so the offset is measured here, as on collection pages.
  const initHeaderOffset = () => {
    const header = document.getElementById('sticky-header');
    if (!header) return;

    const root = document.documentElement;
    const update = () => {
      // The open phone menu grows the header to the full screen; keep the resting offset.
      if (document.body.classList.contains('cws-header-navigation-open')) return;
      root.style.setProperty('--cws-header-offset', `${Math.round(header.getBoundingClientRect().height)}px`);
    };

    update();
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(header);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }
  };

  // KK-1862: the Inbox chat button and the back to top arrow stay clear of the pinned checkout bar
  // on phones and of the checkout block in the order summary on wider screens. The chat moves
  // through the Inbox app's own --shopify-chat-activator-offset on its element, the arrow through
  // its bottom offset. KK-1910: a translate on the chat button left its invisible wrapper behind,
  // over the arrow and the checkout buttons, where it took their taps.
  // A control that would still sit on the checkout block fades out until the block has passed:
  // on phones the block scrolls under the controls, and lifting them over it would make them jump.
  const initFloatingControls = () => {
    const gap = 12;
    const root = document.documentElement;
    const blockParts = [
      '#main-cart-items #js-contents',
      '#main-cart-items #checkout-submit',
      '#main-cart-items .cws-cart-page__express',
      '#main-cart-items .icon-payment',
    ];
    let frame = 0;
    let arrowBase = null;
    let chatBase = null;

    const rectInView = (element) => {
      if (!element) return null;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight) return null;
      return rect;
    };

    // Totals, Check out, the Shop Pay, Apple Pay and Google Pay buttons and the payment icons as one
    // box, so any number of payment buttons and the gaps between them are covered.
    const checkoutBlock = () => {
      const rects = blockParts.map((selector) => rectInView(document.querySelector(selector))).filter(Boolean);
      if (!rects.length) return null;
      return {
        top: Math.min(...rects.map((rect) => rect.top)),
        bottom: Math.max(...rects.map((rect) => rect.bottom)),
        left: Math.min(...rects.map((rect) => rect.left)),
        right: Math.max(...rects.map((rect) => rect.right)),
      };
    };

    // KK-1910: after the chat has been opened and closed, the app renders its button one level down,
    // inside the shadow root of its nested shopify-agent-runtime.
    const chatButton = (chat) => {
      const shadow = chat && chat.shadowRoot;
      if (!shadow) return null;
      const runtime = shadow.querySelector('shopify-agent-runtime');
      return shadow.querySelector('[part~="activator"]')
        || (runtime && runtime.shadowRoot ? runtime.shadowRoot.querySelector('[part~="activator"]') : null);
    };

    const overlaps = (rect, block) =>
      Boolean(rect && block && rect.left < block.right && rect.right > block.left && rect.top < block.bottom && rect.bottom > block.top);

    const update = () => {
      frame = 0;
      const arrow = document.querySelector('.scroll_top');
      const chat = document.querySelector('shopify-chat');
      if (!arrow && !chat) return;

      const viewportHeight = window.innerHeight;
      if (arrow && arrowBase === null) arrowBase = Number.parseFloat(window.getComputedStyle(arrow).bottom) || 40;
      if (chat && chatBase === null) {
        chatBase = Number.parseFloat(window.getComputedStyle(chat).getPropertyValue('--shopify-chat-activator-offset')) || 80;
      }

      // KK-1910: the arrow is hidden until the page scrolls, so it measures 0 at first; fall back to its
      // real size (44px, 56px from 768px), or the chat is placed for the smaller arrow and touches it.
      const arrowFallback = window.innerWidth >= 768 ? 56 : 44;
      const arrowHeight = arrow ? arrow.getBoundingClientRect().height || arrowFallback : 0;
      const chatRect = rectInView(chatButton(chat));
      const chatHeight = chatRect ? chatRect.height : 46;
      const baseArrow = arrowBase === null ? 40 : arrowBase;
      const baseChat = chatBase === null ? 80 : chatBase;

      // Offset from the bottom of the screen that the stack of controls must start above.
      let floor = baseArrow;
      const block = checkoutBlock();
      const bar = rectInView(document.querySelector('#main-cart-items .cws-cart-bar'));
      if (bar) {
        floor = Math.max(floor, viewportHeight - bar.top + gap);
      } else if (block) {
        const stackTop = viewportHeight - Math.max(baseChat + chatHeight, baseArrow + arrowHeight) - gap;
        const stackLeft = window.innerWidth - 140;
        if (block.right > stackLeft && block.bottom > stackTop) {
          // Lift over the whole block only while the lifted stack still fits under the header.
          const headerOffset = Number.parseFloat(root.style.getPropertyValue('--cws-header-offset')) || 0;
          const stackHeight = (arrowHeight || 44) + gap + chatHeight;
          const lifted = viewportHeight - block.top + gap;
          if (viewportHeight - lifted - stackHeight >= headerOffset + gap) floor = Math.max(floor, lifted);
        }
      }

      if (arrow) arrow.style.setProperty('bottom', `${Math.round(floor)}px`);
      if (chat) {
        const chatOffset = floor + (arrowHeight || 44) + gap;
        chat.style.setProperty('--shopify-chat-activator-offset', `${Math.round(Math.max(baseChat, chatOffset))}px`, 'important');
      }

      // Measured after the move above; fading changes opacity and visibility only, so the boxes stay put.
      const chatActivator = chatButton(chat);
      root.classList.toggle('cws-cart-chat-covered', overlaps(rectInView(chatActivator), block));
      root.classList.toggle('cws-cart-arrow-covered', overlaps(rectInView(arrow), block));
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // The cart re-renders replace the summary and the bar; the Inbox chat loads late.
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    schedule();
  };

  const init = () => {
    initHeaderOffset();
    if (document.getElementById('main-cart-items')) initFloatingControls();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
